<template>
  <b-modal
    ref="change-schedule-time-modal"
    id="change-schedule-time-modal"
    class="change-schedule-time-modal"
    hide-header
    centered
    no-close-on-backdrop
    no-close-on-esc
  >
    <div
      class="d-flex justify-content-between top-modal"
      style="margin-top: 10px;"
    >
      <div>
        <h4 class="font-medium text-primary">
          Edit Schedule Time
        </h4>
        <p class="mb-0 text-placeholder">
          Set a new bid for selected ad groups in selected campaigns.
        </p>
      </div>
      <div>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hideModal"
        />
      </div>
    </div>
    <div class="choose-video-content my-2">
      <b-form-radio
        v-model="scheduleType"
        :disabled="isCampaignBudgetTotal || isAdGroupBudgetTotal"
        name="some-radios3"
        value="SCHEDULE_FROM_NOW"
        class="custom-control"
      >
        Set start time to run ad group continuously

        <div v-if="scheduleType === 'SCHEDULE_FROM_NOW'">
          <flat-pickr
            v-model="startDate"
            :class="['form-control', 'mt-1', { 'is-invalid': messageErrorDate }]"
            :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', allowInput: true, allowInvalidPreload: true, minDate: todayStartOfDay }"
            @input="changeStartDate"
          />
          <small
            v-if="messageErrorDate"
            class="text-danger d-block mt-25"
          >{{ messageErrorDate }}</small>
          <small class="text-placeholder mt-25">
            Ad delivery is based on your registered account timezone ({{ etcToUTC(adAccountSelected.timezone) }}).
          </small>
        </div>
      </b-form-radio>
      <b-form-radio
        v-model="scheduleType"
        name="some-radio9"
        value="SCHEDULE_START_END"
        class="custom-control mt-2"
      >
        Set start time and end time to run ad group

        <div v-if="scheduleType === 'SCHEDULE_START_END'">
          <flat-pickr
            v-model="rangeDate"
            :class="['form-control', 'mt-1', { 'is-invalid': messageErrorRangeDate }]"
            :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', mode: 'range', allowInput: true, allowInvalidPreload: true, minDate: todayStartOfDay }"
            @input="changeRangeDate"
          />
          <small
            v-if="messageErrorRangeDate"
            class="text-danger d-block mt-25"
          >{{ messageErrorRangeDate }}</small>
          <small class="text-placeholder mt-25">
            Ad delivery is based on your registered account timezone ({{ etcToUTC(adAccountSelected.timezone) }}).
          </small>
        </div>
      </b-form-radio>
    </div>

    <!-- Action Buttons -->
    <template #modal-footer>
      <div class="d-flex justify-content-end">
        <btn-loading
          class="mr-1 border-0"
          variant-convert="btn-back"
          pill
          @click="hideModal"
        >
          Cancel
        </btn-loading>
        <btn-loading
          class="ml-1"
          variant-convert="btn-submit"
          type="submit"
          pill
          @click="confirmSelection"
        >
          Apply
        </btn-loading>
      </div>
    </template>
  </b-modal>
</template>
<script>
import { createNamespacedHelpers } from 'vuex'
import {
  BModal,
  BFormRadio,
} from 'bootstrap-vue'
import BtnLoading from '@/layouts/components/BtnLoading.vue'
import flatPickr from 'vue-flatpickr-component'
import dayjs from 'dayjs'
import campaignMixin from '@/mixins/campaignMixin'
import { BUDGET_MODE } from '@/constants'

const { mapActions, mapGetters } = createNamespacedHelpers('campaign')

export default {
  components: {
    BModal,
    BtnLoading,
    BFormRadio,
    flatPickr,
  },
  mixins: [campaignMixin],
  props: {
    campaigns: {
      type: Array,
      default: () => [],
      required: true,
    },

    adAccountSelected: {
      type: Object,
      default: () => ({}),
      required: true,
    },

    isCampaignBudgetTotal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentAdGroup: null,
      currentCampaignIndex: null,
      currentAdGroupIndex: null,
      scheduleType: 'SCHEDULE_FROM_NOW', // or 'SCHEDULE_START_END'

      startDate: null,
      rangeDate: null,
      now: new Date(),

      messageErrorDate: '',
      messageErrorRangeDate: '',
    }
  },

  computed: {
    ...mapGetters(['listIdentity', 'listVideoTikTok', 'status', 'message', 'loading']),
    todayStartOfDay() {
      return dayjs().subtract(1, 'day').tz(this.adAccountSelected.timezone).toDate()
    },

    isAdGroupBudgetTotal() {
      return this.currentAdGroup?.adgroup?.budgetMode === BUDGET_MODE.BUDGET_MODE_TOTAL
    },
  },

  methods: {
    ...mapActions(['getListIdentity', 'getListVideoTikTok']),
    changeStartDate() {
      const tz = this.adAccountSelected.timezone
      const start = dayjs.tz(this.startDate, tz)
      const nowTz = dayjs().tz(tz)
      if (start.isBefore(nowTz, 'minute')) {
        this.messageErrorDate = "Start time can't be earlier than the current time"
      } else {
        this.messageErrorDate = ''
      }
    },

    changeRangeDate() {
      const tz = this.adAccountSelected.timezone
      const [rangeStart, rangeEnd] = this.rangeDate.split('to').map(s => s.trim())
      const start = dayjs.tz(rangeStart, tz)
      const end = dayjs.tz(rangeEnd, tz)
      const nowTz = dayjs().tz(tz)

      if (start.isBefore(nowTz, 'minute')) {
        this.messageErrorRangeDate = "Start time can't be earlier than the current time"
      } else if (!start.isBefore(end, 'minute')) {
        this.messageErrorRangeDate = 'Start date must be earlier than end date.'
      } else {
        this.messageErrorRangeDate = ''
      }
    },

    parseToDate(value) {
      const tz = this.adAccountSelected.timezone
      if (!value) return null
      if (dayjs.isDayjs(value)) return value.tz(tz)
      if (value instanceof Date) return dayjs(value).tz(tz)
      const num = Number(value)
      if (!Number.isNaN(num)) {
        const millis = num < 1000000000000 ? num * 1000 : num
        const d = dayjs(millis).tz(tz)
        return d.isValid() ? d : null
      }
      // try explicit format then generic tz parse
      let d = dayjs.tz(String(value).trim(), 'YYYY-MM-DD HH:mm:ss', tz)
      if (!d.isValid()) d = dayjs.tz(String(value).trim(), tz)
      return d.isValid() ? d : null
    },
    initData() {
      if (this.currentAdGroup) {
        const start = this.currentAdGroup?.adgroup?.scheduleStartTime
        const end = this.currentAdGroup?.adgroup?.scheduleEndTime

        const startDate = this.parseToDate(start)
        const endDate = this.parseToDate(end)

        const tz = this.adAccountSelected.timezone
        this.startDate = startDate ? startDate.tz(tz).format('YYYY-MM-DD HH:mm') : null
        this.rangeDate = startDate && endDate ? `${startDate.tz(tz).format('YYYY-MM-DD HH:mm')} to ${endDate.tz(tz).format('YYYY-MM-DD HH:mm')}` : null

        this.scheduleType = this.currentAdGroup?.adgroup?.scheduleType
      }
    },

    resetModal() {
      this.currentAdGroup = null
    },

    hideModal() {
      this.$refs['change-schedule-time-modal'].hide()
      this.resetModal()
    },

    async showModal(campaignIndex, adgroupIndex) {
      // Set current ad group from passed parameters
      this.currentCampaignIndex = campaignIndex
      this.currentAdGroupIndex = adgroupIndex

      if (campaignIndex !== null && adgroupIndex !== null) {
        const campaign = this.campaigns[campaignIndex]
        const adgroup = campaign?.adgroupList[adgroupIndex]

        this.currentAdGroup = {
          campaignIndex,
          adgroupIndex,
          adgroup,
        }

        this.initData()
      }

      this.$refs['change-schedule-time-modal'].show()
    },

    confirmSelection() {
      const campaignIndex = this.currentAdGroup?.campaignIndex
      const adgroupIndex = this.currentAdGroup?.adgroupIndex
      const adgroup = this.campaigns[campaignIndex]?.adgroupList[adgroupIndex]

      adgroup.scheduleType = this.scheduleType

      if (this.scheduleType === 'SCHEDULE_FROM_NOW') {
        adgroup.scheduleStartTime = dayjs(this.startDate).format('YYYY-MM-DD HH:mm:ss')
        adgroup.scheduleEndTime = dayjs(this.startDate).add(1, 'year').format('YYYY-MM-DD HH:mm:ss')
      } else if (this.scheduleType === 'SCHEDULE_START_END') {
        const start = this.rangeDate?.split('to')[0].trim()
        const end = this.rangeDate?.split('to')[1].trim()
        adgroup.scheduleStartTime = dayjs(start).format('YYYY-MM-DD HH:mm:ss')
        adgroup.scheduleEndTime = dayjs(end).format('YYYY-MM-DD HH:mm:ss')
      }
      this.hideModal()
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
#change-schedule-time-modal {
  .modal-content {
    .modal-body {
      height: 100%;
    }

    .form-control:focus, .input-group:focus-within .form-control {
      border-color: #e0e0e5 !important;
    }
  }
}
</style>
